import React from 'react';

import { useStoreActions, useStoreState, useStore } from '../store';

interface ChromecastProviderProps {
  children: React.ReactNode;
}

const Context = React.createContext({ initializeCastApi: () => {} });

export const useChromecast = () => React.useContext(Context);

export function ChromecastProvider(props: ChromecastProviderProps) {
  const store = useStore();
  const chromecastAppId = useStoreState(state => state.config.chromecastAppId);
  const chromecastActions = useStoreActions(actions => actions.chromecast);

  const [castInitialized, setCastInitialized] = React.useState(false);

  /**
   * Initialize the Cast SDK API and delegates the
   * rest of Chromecast functionality to the chromecast store actions.
   */
  const initializeCastApi = React.useCallback(() => {
    if (!chromecastAppId || !cast?.framework || castInitialized) return;

    setCastInitialized(true);

    const castContext = cast.framework.CastContext.getInstance();

    castContext.setOptions({
      receiverApplicationId: chromecastAppId,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
    });

    const castStateHandler = (event: cast.framework.CastStateEventData) => {
      chromecastActions.setCastState(event.castState);

      const { NO_DEVICES_AVAILABLE, NOT_CONNECTED } = cast.framework.CastState;

      const previousCastState = store.getState().chromecast.castState;

      const shouldResumeLocalPlayback =
        event.castState === NOT_CONNECTED &&
        previousCastState !== NOT_CONNECTED &&
        previousCastState !== NO_DEVICES_AVAILABLE;

      if (shouldResumeLocalPlayback) {
        chromecastActions.resumeLocalPlayback();
      }
    };

    const sessionStateHandler = (event: cast.framework.SessionStateEventData) => {
      switch (event.sessionState) {
        case cast.framework.SessionState.SESSION_STARTED:
          chromecastActions.initialize(store);
          chromecastActions.loadMedia();
          break;
        case cast.framework.SessionState.SESSION_RESUMED:
          chromecastActions.initialize(store);
          chromecastActions.stop();
          break;
      }
    };

    castContext.addEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, castStateHandler);
    castContext.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, sessionStateHandler);

    return () => {
      castContext.removeEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, castStateHandler);
      castContext.removeEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, sessionStateHandler);
    };
  }, [chromecastAppId, chromecastActions, store, castInitialized]);

  React.useEffect(() => {
    let cleanupCastApiListeners: (() => void) | undefined;

    /**
     * Inserts the Cast SDK script tag and registers
     * the __onGCastApiAvailable listener on the web page.
     */
    function setupCastSdk() {
      const globalWindow: any = window;

      if (!globalWindow.__onGCastApiAvailable) {
        globalWindow.__onGCastApiAvailable = function(isAvailable: boolean) {
          if (isAvailable) {
            cleanupCastApiListeners = initializeCastApi();
          }
        };
      }

      if (!document.getElementById('web-video-player-chromecast-sdk')) {
        const script = document.createElement('script');
        script.id = 'web-video-player-chromecast-sdk';
        script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
        document.body.appendChild(script);
      }
    }

    /**
     * Manually resume the Cast session when the video player
     * has been closed and opened without reloading the page.
     */
    function resumeSessionManually() {
      if (!chromecastAppId) return;

      if (typeof cast === 'undefined') return;

      const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

      if (castSession) {
        chromecastActions.initialize(store);
        chromecastActions.stop();
      }
    }

    setupCastSdk();
    resumeSessionManually();

    return () => {
      if (cleanupCastApiListeners) {
        cleanupCastApiListeners();
      }
    };
  }, [store, chromecastActions, chromecastAppId, initializeCastApi]);

  return <Context.Provider value={{ initializeCastApi }}>{props.children}</Context.Provider>;
}
